/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { TreeModel, ITreeOptions, ITreeState, IActionMapping, TREE_ACTIONS } from '@circlon/angular-tree-component';
import { Component, Input, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";

import { Rule } from "../../../models/rule";
import { ZoneInstance } from "../../../models/zone-instance";
import { ZoneService } from "../../../services/zone.service";
import { LocationInstance } from "../../../models/location-instance";
import { LocationService } from "../../../services/location.service";


const actionMapping: IActionMapping = {
    mouse: {
        dblClick: (tree, node, $event) => {
            if (node.hasChildren) {
                TREE_ACTIONS.TOGGLE_EXPANDED(tree, node, $event);
            }
        },
        click: (tree, node, $event) => {
            if ($event.shiftKey || $event.ctrlKey) {
                TREE_ACTIONS.TOGGLE_EXPANDED(tree, node, $event);
            } else {
                TREE_ACTIONS.TOGGLE_ACTIVE_MULTI(tree, node, $event);
            }
        }
    }
};

const debug = false;

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-location-filter-editor',
  templateUrl: './location-filter-editor.component.html',
  styleUrls: [
      './location-filter-editor.component.css'
  ]
})
export class LocationFilterEditorComponent {

    @Input()
    rule: Rule;

    @Input()
    set locations(locations: LocationInstance[]) {
        this.log("Setting Locations: " + (locations == null? "*NULL*" : locations.length));
        this._locations = locations;
        this.refreshNodes();
    }
    get locations(): LocationInstance[] {
        return this._locations;
    }
    private _locations: LocationInstance[];


    //Used to indicate whether the body (Tree) of the "Location Filter" should be displayed or hidden
    showBody = true;
    

    /*
     * Variables used for the "Tree" used for the Location Filter
     */
    //"Tree Nodes" created / displayed
    nodes: LocationFilterTreeNode[] = [];
    options: ITreeOptions = { 
        actionMapping
    };
    private treeModel: TreeModel = null;
    private expandAndSelectNodes = true;



    constructor(
        private route: ActivatedRoute,
        private locationService: LocationService,
        private zoneService: ZoneService
    ) { }



    /**
     * Called to refresh the Tree Nodes using the @Input Locations as options
     */
    private async refreshNodes(): Promise<void> {
        this.log("refreshNodes() ");

        /*
         * Update the Flag indicating that the Tree needs to be expanded and the nodes selected based on the Rule.
         * Needed since the Node Options are changing, which only happens during the initial display or when the available
         * Locations are loaded.
         */
        this.expandAndSelectNodes = true;

        const nodes: LocationFilterTreeNode[] = [];
        if (this.locations != null) {
            let nodeIndex = 0;
            for (const location of this.locations) {
                const locationNode = this.createNodeForLocation(location, ++nodeIndex);
                if (location.zones != null && location.zones.length > 0) {
                    for (const zone of location.zones) {
                        locationNode.addChild(this.createNodeForZone(zone, ++nodeIndex));
                    }
                }
                nodes.push(locationNode);
            }
        }
        this.nodes = nodes;
    }
    
    /**
     * Called whenever the Angular Tree Component's State has changed.
     * https://angular2-tree.readme.io/v7.0.0/docs/events#stateChange
     * Event coming in is the State (ITreeState) of the Tree Model
     * 
     * Used to update the Rule's Locations / Zones.
     * Need to be careful here since these events get thrown occasionally when the Filter is 
     * being removed and before the Rule's Locations / Zones have their corresponding Tree Node selected.
     */
    onStateChange(event: any): void {
        this.log("onStateChange()\n\tTree Model Null? " + (this.treeModel == null) + "\n\tExpanding and Selecting? " + this.expandAndSelectNodes);
        if (this.treeModel != null && !this.expandAndSelectNodes) {

            //Get the Locations / Zones that are currently selected
            const locations: LocationInstance[] = [];
            const zones: ZoneInstance[] = [];
            {
                //Get the "Active Nodes" from the Tree Model (The changes from the State Change are already reflected there)
                //We consider "Active Nodes" as the nodes that are selected...
                const activeNodes = this.treeModel.getActiveNodes();
                if (activeNodes != null) {
                    for (const activeNode of activeNodes) {
                        const filterNode: LocationFilterTreeNode = activeNode.data;
                        if (filterNode.location != null) {
                            locations.push(filterNode.location);
                        } else if (filterNode.zone != null) {
                            zones.push(filterNode.zone);
                        }
                    }
                    locations.sort((a: LocationInstance, b: LocationInstance) => { 
                        return a.id - b.id;
                    });
                    zones.sort((a: ZoneInstance, b: ZoneInstance) => {
                        return a.id - b.id;
                    });
                }
            }

            if (debug) {
                const locationIds: string[] = [];
                for (const location of locations) {
                    locationIds.push("{'id': " + location.id + ", 'name': '" + location.name + "'}");
                }
                const zoneIds: string[] = [];
                for (const zone of zones) {
                    zoneIds.push("{'id': " + zone.id + ", 'name': '" + zone.name + "'}");
                }
                this.log("Updating Rule\n\tLocations: [" + locationIds + "]\n\tZones: [" + zoneIds + "]");
            }
            this.rule.locations = locations;
            this.rule.zones = zones;
        }
    }

    /**
     * Called whenever the Angular Tree Component's Model was updated.
     * https://angular2-tree.readme.io/v7.0.0/docs/events#updatedata
     * Event coming in can reference the Tree Model using the "treeModel" property
     * 
     * Used to initially expand the options and select the ones already selected by the Rule. 
     * Main focus here is when editing an existing Rule with a Location Filter. 
     * (onInitialized() will be called before the Locations are set)
     */
    onUpdateData(event: any): void {
        this.log("onUpdateData()");

        //Expand the Tree and select the Nodes (if needed, indicated by flag set when Node Options are changed)
        this.selectNodesAndExpandTree();
    }

    /**
     * Called when the Angular Tree Component has finished initializing.  
     * https://angular2-tree.readme.io/v7.0.0/docs/events#initialized
     * Event coming in can reference the Tree Model using the "treeModel" property
     * 
     * Used to do the following:
     *   1) Obtain the reference to the "TreeModel" being used to display the Locations / Zones so
     *      that it can be used in "onStateChange()"
     *   2) Expand the Tree and select the Locations / Nodes already selected by the Rule.
     *      Main focus here is when adding a new Location Filter to a Rule.  
     *      (onUpdateData() won't be called since the "nodes" never changes after it is created)
     */
    onInitialized(event: any): void {
        this.log("onInitialized()");

        //Store a reference to the Tree Model so that it can be used in the "onStatusChange()" handler. 
        this.treeModel = event.treeModel;

        //Expand the Tree and select the Nodes (if needed, indicated by flag set when Node Options are changed)
        this.selectNodesAndExpandTree();
    }

    /**
     * Expands the Location Filter Tree and selects the Location / Zone Nodes already selected for the Rule.
     */
    private selectNodesAndExpandTree(): void {
        this.log("selectNodesAndExpandTree()\n\tExpanding and Selecting? " + this.expandAndSelectNodes)
        if (this.expandAndSelectNodes) {
            let message = "selectNodesAndExpandTree()";
            message = message + "\n\tRule already has the following selected:";
            if (this.rule.locations != null) {
                for (const location of this.rule.locations) {
                    message = message + "\n\t\tLocation: '" + location.name + "'";
                }
            }
            if (this.rule.zones != null) {
                for (const zone of this.rule.zones) {
                    message = message + "\n\t\tZone: '" + zone.name + "'";
                }
            }

            message = message + "\n\tSelecting the following:"
            //Select the Locations / Zones that are already selected for the Rule
            const activeNodeIds: {[propKey: number]: boolean} = {};
            if (this.nodes != null && this.nodes.length > 0) {
                for (const locationNode of this.nodes) {
                    const locationSelected = this.rule.locations != null && locationNode.location != null? 
                        this.rule.locations.find(location => {
                            const rv = location.id == locationNode.location.id;
                            return rv;
                        }) != null : false;
                    if (locationSelected) {
                        message = message + "\n\t\tLocation: '" + locationNode.location.name + "'";
                        activeNodeIds[locationNode.id] = true;
                    }
                    if (locationNode.children != null) {
                        for (const zoneNode of locationNode.children) {
                            const zoneSelected = this.rule.zones != null && zoneNode.zone != null? 
                                this.rule.zones.find(zone => zone.id == zoneNode.zone.id) != null : false;
                            if (zoneSelected) {
                                message = message + "\n\t\tZone: '" + zoneNode.zone.name + "'";
                                activeNodeIds[zoneNode.id] = true;
                            }
                        }
                    }
                }
            }

            this.log(message);
            //Set the "selected" (Active) Nodes in the Tree
            this.treeModel.setState({ ...this.treeModel.getState(), activeNodeIds });

            //Expand the Tree fully
            this.treeModel.expandAll();

            //Set the Flag used to indicate if the Tree should be expanded and the nodes selected to false since this just happened...
            //Needs to be done after the setting the "State" on the Tree Model and expanding it since "onStatusChange()" will be called and should be ignored.  
            this.expandAndSelectNodes = false;
        }
    }

    /**
     * Called to remove the "Location Filter" from the Rule.
     */
    onRemove(): void {
        //Set the Tree Model to Null since it is being hidden.  This can then be checked in "onStatusChange()" to avoid updating the Rule's Locations / Zones as that is called after setting these to null.
        this.treeModel = null;

        this.log("Updating Rule... Locations = null, Zones = null...");
        this.rule.locations = null;
        this.rule.zones = null;
    }

    /**
     * Creates a LocationFilterTreeNode for the given Location
     */
    createNodeForLocation(location: LocationInstance, uniqueId: number): LocationFilterTreeNode {
        const rv = new LocationFilterTreeNode();
        rv.id = uniqueId;
        rv.name = location.name;
        rv.location = location;
        return rv;
    }

    /**
     * Creates a LocationFilterTreeNode for the given Zone
     */
    createNodeForZone(zone: ZoneInstance, uniqueId: number): LocationFilterTreeNode {
        const rv = new LocationFilterTreeNode();
        rv.id = uniqueId;
        rv.name = zone.name;
        rv.zone = zone;
        return rv;
    }

    log(message: string): void {
        if (debug) {
            console.log(message);
        }
    }

}

/**
 * Represents the minimum amount of Data that needs to be entered / specified
 * for a "Tree Node" using the Angular Tree Component
 */
interface ITreeNode {
    //Unique ID for the node.
    id: number;
    //Will be displayed by default in the tree.
    name: string;
    //An array of the node's children.
    children: ITreeNode[];
}

/**
 * Use Case Specific implementation for the Tree Node used in the 
 * Angular Tree Node Component
 */
class LocationFilterTreeNode implements ITreeNode {
    //ITreeNode variables
    id: number;
    name: string;
    children: LocationFilterTreeNode[];

    //Variables related to this specific use-case
    location: LocationInstance;
    zone: ZoneInstance;

    addChild(node: LocationFilterTreeNode): void {
        if (this.children == null) {
            this.children = [];
        }
        this.children.push(node);
    }
}
